import {
    HttpRequest,
    HttpInterceptor,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse,
    HttpHeaders,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { AppStoreService } from '../store/app-store.service';
import { AuthService } from '../service/auth.service';
import { SnackBarService } from '@core/service/snack-bar.service';
import { ErrorHandlerService } from '@core/service/error-handler.service';
import { ErrorResponse } from '@core/models/error-response.model';
import { InterceptorService } from '@core/service/interceptor.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    private readonly _interceptorService = inject(InterceptorService);

    constructor(
        private readonly _authService: AuthService,
        private readonly _appStoreService: AppStoreService,
        private readonly _snackBarService: SnackBarService,
        private readonly _errorHandlerService: ErrorHandlerService,
    ) {}

    intercept( request: HttpRequest<unknown>, next: HttpHandler ): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((err: HttpErrorResponse) => {
                let errorMessage: string;
                if (
                    [400, 500].includes(err.status) 
                    && this._appStoreService.token() != null
                ) {
                    console.log('HTTPErrorInterceptor:' + err.status);
                    console.error(err);
                    if(!this._interceptorService.noErrorSnackbar(request, err)){
                        this._snackBarService.errorMessage(err.error?.message ?? err.message);
                    }
                    this.setServerError(err);
                    return throwError(() => new HttpErrorResponse(this.getHttpErrorResponse(err)));
                }

                // 401 - /totp/verify
                if (
                    [401].includes(err.status) 
                    && this._appStoreService.token() != null
                    && this._interceptorService.no401TotpError(request, err)
                ) {
                    console.log('HTTPErrorInterceptor:' + err.status);
                    console.error(err);
                    if(!this._interceptorService.noErrorSnackbar(request, err)){
                        this._snackBarService.errorMessage(err.error?.message ?? err.message);
                    }
                    this.setServerError(err);
                    return throwError(() => new HttpErrorResponse(this.getHttpErrorResponse(err)));
                }

                // 401 - other
                if (
                    [401].includes(err.status) 
                    && this._interceptorService.no401Error(request, err)
                ) {
                    console.log('HTTPErrorInterceptor:' + err.status);
                    console.error(err);
                    if(!this._interceptorService.noErrorSnackbar(request, err)){
                        this._snackBarService.errorMessage(err.error?.message ?? err.message);
                    }
                    this.setServerError(err);
                    return throwError(() => new HttpErrorResponse(this.getHttpErrorResponse(err)));
                }

                if (
                    [403].includes(err.status) 
                    && !this._interceptorService.no403Error(request, err)
                ) {
                    console.log('HTTPErrorInterceptor:' + err.status);
                    this._authService.logout('common.error.api.403');
                    return throwError(() => new HttpErrorResponse(this.getHttpErrorResponse(err)));
                }

                if (
                    err.status === 401
                    || err.status === 408
                    || err.status === 503
                    // || err.status === 404
                ) {
                    console.log('HTTPErrorInterceptor: ' + err.status + ' ' + err.status);
                    console.error(err);

                    if (err.status === 401) {
                        if (!this._authService.isAuthenticated()) {
                            errorMessage = 'common.error.session.expired.authenticated'; // ERROR_401_NOT_AUTHENTICATED
                        } else {
                            errorMessage = 'common.error.session.expired.notauthenticated'; // ERROR_401_AUTHENTICATED
                        }
                    } else {
                        errorMessage = 'common.error.api.unavailable'; // ERROR_OTHER
                    }

                    // TODO: mi történjen ilyenkor?
                    this._authService.logout(errorMessage);
                    return throwError(() => new HttpErrorResponse(this.getHttpErrorResponse(err)));
                }

                // timeout
                if (err.status.toString().startsWith('0') || err.status === 504) {
                    console.log('HTTPErrorInterceptor:' + err.status);
                    this._authService.logout('common.error.api.unavailable'); // ERROR_OTHER
                    return throwError(() => new HttpErrorResponse(this.getHttpErrorResponse(err)));
                }

                return throwError(() => new HttpErrorResponse(this.getHttpErrorResponse(err)));
            })
        );
    }

    private getHttpErrorResponse(err: HttpErrorResponse): {
        error?: any;
        headers?: HttpHeaders;
        status?: number;
        statusText?: string;
        url?: string;
        redirected?: boolean;
    }{
        return {
            error: err.error,
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url ?? undefined,
            redirected: err.redirected,
        }
    }

    private setServerError(err: HttpErrorResponse){
        if(err.error != null){
            const apiErrorMessages: ErrorResponse[] = [];
            apiErrorMessages.push({
                error: err.error.error ?? '',
                message: err.error.message ?? '',
                timestamp: err.error.timestamp ?? '',
            });
            this._errorHandlerService.setApiErrorMessages(apiErrorMessages);
        }
    }
}
